import { AdminApiError, type AdminEnvelope } from './_core.js';

export interface LinkedLlmCall {
  id: string;
  provider: string;
  model: string;
  inputTokens: number;
  outputTokens: number;
  cacheCreateTokens: number;
  cacheReadTokens: number;
  latencyMs: number | null;
  ttftMs: number | null;
  retries: number;
  costNano: string;
  /** Raw provider cost (sin markup). Migration 0064 / Fase 34. */
  providerCostNano?: string;
  /** Markup bps efectivo aplicado al persistir la fila. */
  markupBps?: number | null;
  createdAt: string;
}

export interface LinkedTtsCall {
  id: string;
  provider: string;
  model: string;
  voiceId: string | null;
  characters: number;
  audioSeconds: string | null;
  latencyMs: number | null;
  costNano: string;
  providerCostNano?: string;
  markupBps?: number | null;
  createdAt: string;
}

export interface LinkedSttCall {
  id: string;
  provider: string;
  model: string;
  audioSeconds: string;
  charactersOut: number | null;
  latencyMs: number | null;
  costNano: string;
  providerCostNano?: string;
  markupBps?: number | null;
  createdAt: string;
}

export interface LinkedCallResponse {
  spanId: string;
  callKind: 'llm' | 'tts' | 'stt';
  callRefId: string;
  row: LinkedLlmCall | LinkedTtsCall | LinkedSttCall;
}

export async function fetchLinkedCall(spanId: string): Promise<LinkedCallResponse | null> {
  const res = await fetch(`/api/admin/spans/${encodeURIComponent(spanId)}/linked-call`, {
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });
  if (res.status === 404) return null;
  const json = (await res.json().catch(() => null)) as AdminEnvelope<LinkedCallResponse> | null;
  if (!res.ok || !json || !('success' in json) || json.success !== true) {
    const code = json && 'error' in json ? json.error.code : 'http_error';
    const msg = json && 'error' in json ? json.error.message : `HTTP ${res.status}`;
    throw new AdminApiError(res.status, code, msg);
  }
  return json.data;
}

export interface SpanIoResponse {
  spanId: string;
  kind: string;
  name: string;
  input: unknown;
  output: unknown;
}

export interface LlmPromptLog {
  spanId: string;
  promptLogId: string;
  turnId: string | null;
  systemPrompt: string;
  messages: unknown;
  responseText: string | null;
  createdAt: string;
}

export async function fetchSpanPromptLog(spanId: string): Promise<LlmPromptLog | null> {
  const res = await fetch(`/api/admin/spans/${encodeURIComponent(spanId)}/prompt-log`, {
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });
  if (res.status === 404) return null;
  const json = (await res.json().catch(() => null)) as AdminEnvelope<LlmPromptLog> | null;
  if (!res.ok || !json || !('success' in json) || json.success !== true) {
    const code = json && 'error' in json ? json.error.code : 'http_error';
    const msg = json && 'error' in json ? json.error.message : `HTTP ${res.status}`;
    throw new AdminApiError(res.status, code, msg);
  }
  return json.data;
}

/**
 * Lee el input/output crudo persistido en el span (GET read-only, sin
 * audit log). El detail pane lo consume automáticamente para mostrar el
 * contenido sin gate. Devuelve null si el span no existe.
 */
export async function fetchSpanIo(spanId: string): Promise<SpanIoResponse | null> {
  const res = await fetch(`/api/admin/spans/${encodeURIComponent(spanId)}/io`, {
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });
  if (res.status === 404) return null;
  const json = (await res.json().catch(() => null)) as AdminEnvelope<SpanIoResponse> | null;
  if (!res.ok || !json || !('success' in json) || json.success !== true) {
    const code = json && 'error' in json ? json.error.code : 'http_error';
    const msg = json && 'error' in json ? json.error.message : `HTTP ${res.status}`;
    throw new AdminApiError(res.status, code, msg);
  }
  return json.data;
}
